// ModalEditar.tsx
// --------------------- Import Globais ---------------------
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// --------------------- Import Antd-Design ---------------------
import {
  Modal,
  Skeleton,
  Form,
  Input,
  Radio,
  Select,
  DatePicker,
  RadioChangeEvent,
} from "antd";

// --------------------- Import Styles ---------------------
import { formStyle, layout, Title } from "../../ViewGerenciarPostagens/styles";

// --------------------- Import Hooks ---------------------
import { useBuscarLeadsById } from "../hooks/useBuscarLeadsById";
import { useAtualizarLead } from "../hooks/useAtualizarLead";

// --------------------- Import Constants ---------------------
import { tipoOptions } from "../constants/tipoOptions";
import { statusOptions } from "../constants/statusOptions";

// --------------------- Import Uteis ---------------------
import { useError } from "../../../uteis/mensagens/useError";
import { useSuccess } from "../../../uteis/mensagens/useSuccess";

// --------------------- Import dayJS ---------------------
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";

// --------------------- Phone Input ---------------------
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

export default function ModalEditar({
  isModalOpen,
  handleFechar,
  leadId,
}: any) {
  const [formLeads] = Form.useForm();
  const changing = useRef<boolean>(false);
  console.log("ModalEditar leadId:", leadId);

  dayjs.extend(customParseFormat);

  // ✅ Busca lead apenas quando modal abre (se hook já faz isso, ok; se não fizer, ainda funciona)
  const { isLoading, leads } = useBuscarLeadsById(leadId, isModalOpen);

  const {
    atualizar,
    isLoading: isLoadingAtualizar,
    error,
    success,
    resetError,
  } = useAtualizarLead();

  const [lead, setLead] = useState<any>({});

  // Radios (se você depois remover, pode tirar esses states)
  const [alugar, setAlugar] = useState<number>();
  const [trocar, setTrocar] = useState<number>();
  const [compra, setCompra] = useState<number>();
  const [venda, setVenda] = useState<number>();

  // ✅ Phone controlado (senão fica vazio / desatualizado)
  const [phone, setPhone] = useState<string>("");

  const leadsChange = (_changedValues: Partial<any>, allValues: any) => {
    changing.current = true;
    if (changing.current) setLead(allValues);
  };

  const alugarChange = (e: RadioChangeEvent) => {
    changing.current = true;
    setAlugar(e.target.value);
    formLeads.setFieldValue("alugar", e.target.value);
  };

  const trocarChange = (e: RadioChangeEvent) => {
    changing.current = true;
    setTrocar(e.target.value);
    formLeads.setFieldValue("trocar", e.target.value);
  };

  const compraChange = (e: RadioChangeEvent) => {
    changing.current = true;
    setCompra(e.target.value);
    formLeads.setFieldValue("compra", e.target.value);
  };

  const vendaChange = (e: RadioChangeEvent) => {
    changing.current = true;
    setVenda(e.target.value);
    formLeads.setFieldValue("venda", e.target.value);
  };

  // ✅ Quando carregar o lead, preenche estado + form + phone
  useEffect(() => {
    if (!isModalOpen) return;

    if (leads) {
      const nextLead = {
        id: leads?.id,
        nome: leads?.nome,
        celular: leads?.celular,
        ultimaDivulgacao: leads?.ultimaDivulgacao,
        alugar: leads?.alugar,
        trocar: leads?.trocar,
        compra: leads?.compra,
        venda: leads?.venda,
        status: leads?.status,
        observacoes: leads?.observacoes,
        tipo: leads?.tipo,
      };

      setLead(nextLead);

      // Radios
      setAlugar(nextLead.alugar);
      setTrocar(nextLead.trocar);
      setCompra(nextLead.compra);
      setVenda(nextLead.venda);

      // Phone
      setPhone(nextLead.celular ?? "");

      // Form values
      formLeads.setFieldsValue({
        ...nextLead,
        ultimaDivulgacao: nextLead.ultimaDivulgacao
          ? dayjs(nextLead.ultimaDivulgacao)
          : null,
      });
    }
  }, [isModalOpen, leads, formLeads]);

  // ✅ Ao fechar modal, limpa (evita “modal só com botões” em aberturas seguintes)
  useEffect(() => {
    if (!isModalOpen) {
      setLead({});
      setPhone("");
      setAlugar(undefined);
      setTrocar(undefined);
      setCompra(undefined);
      setVenda(undefined);
      formLeads.resetFields();
    }
  }, [isModalOpen, formLeads]);

  const { showError, contextHolder: errorContextHolder } = useError();
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isModalOpen && success && !successDisplayed) {
      showSuccess("Lead atualizado com sucesso!");
      setSuccessDisplayed(true);

      setTimeout(() => {
        navigate(0);
      }, 2000);
    }

    if (isModalOpen && error && !errorDisplayed) {
      showError("Não foi possível atualizar o Lead!");
      setErrorDisplayed(true);

      setTimeout(() => {
        setErrorDisplayed(false);
        resetError();
      }, 3000);
    }

    if (!isModalOpen) {
      setSuccessDisplayed(false);
      setErrorDisplayed(false);
    }
  }, [
    isModalOpen,
    success,
    error,
    showSuccess,
    showError,
    successDisplayed,
    errorDisplayed,
    navigate,
    resetError,
  ]);

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      cancelText="Cancelar"
      okText="Atualizar"
      onOk={() => {
        if (!leadId) return;
        // garante que o celular do PhoneInput vai junto
        const payload = { ...lead, celular: formLeads.getFieldValue("celular") ?? phone };
        atualizar(leadId, payload);
      }}
      confirmLoading={isLoadingAtualizar}
      width={700}
      destroyOnClose
      maskClosable={false}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", paddingTop: 12 }}
      okButtonProps={{
        style: { backgroundColor: "#003644", borderColor: "#003644", color: "white" },
      }}
      cancelButtonProps={{
        style: { backgroundColor: "#f44336", borderColor: "#f44336", color: "white" },
      }}
    >
      {isLoading && <Skeleton active />}

      {!isLoading && !leads && (
        <div style={{ padding: 12, color: "#999" }}>
          Lead não encontrado. Verifique se você selecionou um Lead (não Usuário).
        </div>
      )}

      {!isLoading && leads && (
        <>
          {errorContextHolder}
          {successContextHolder}

          <Form
            {...layout}
            form={formLeads}
            initialValues={lead}
            onValuesChange={leadsChange}
            name="lead"
            style={formStyle}
          >
            <Title>Informações</Title>

            <Form.Item name="nome" label="Nome" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item name="celular" label="Celular" className="formItems">
              <PhoneInput
                country={"br"}
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  formLeads.setFieldValue("celular", v);
                }}
                placeholder="Digite seu telefone"
                inputStyle={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              name="ultimaDivulgacao"
              label="Ultima Divulgação"
              className="formItems"
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="alugar" label="Alugar" className="formItems">
              <Radio.Group onChange={alugarChange} value={alugar}>
                <Radio value={0}>Não</Radio>
                <Radio value={1}>Sim</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="trocar" label="Trocar" className="formItems">
              <Radio.Group onChange={trocarChange} value={trocar}>
                <Radio value={0}>Não</Radio>
                <Radio value={1}>Sim</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="compra" label="Compra" className="formItems">
              <Radio.Group onChange={compraChange} value={compra}>
                <Radio value={0}>Não</Radio>
                <Radio value={1}>Sim</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="venda" label="Venda" className="formItems">
              <Radio.Group onChange={vendaChange} value={venda}>
                <Radio value={0}>Não</Radio>
                <Radio value={1}>Sim</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name="status" label="Status" className="formItems">
              <Select placeholder="Status">
                {statusOptions.map((status) => (
                  <Select.Option key={status.id} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="observacoes" label="Observações" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item name="tipo" label="Tipo" className="formItems">
              <Select placeholder="Tipo">
                {tipoOptions.map((tipo) => (
                  <Select.Option key={tipo.id} value={tipo.value}>
                    {tipo.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
